<template>
  <div class="absolute w-full h-full overflow-auto scrollbar-gutter-stable px-2" ref="stageContainerEl">
    <div class="absolute min-w-full min-h-full" ref="stageContentEl" @click="deselect">
      <div class="flex flex-1">
        <div class="flex-center shrink-0 w-full select-none">
          <div v-if="initialized">
            <div class="h-14 sticky top-0 z-20 pointer-events-none" @click.stop>
              <surface-editor-element-toolbar
                v-if="selectedElement"
              />
            </div>
            <surface-editor-surface
              v-for="(surface, index) in surfaces" :key="surface.id"
              :surface="surface"
              :class="{'pt-6': index}"
              class="z-[1]"
            />
            <surface-editor-context-menu />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import {storeToRefs} from 'pinia';
  import debounce from 'lodash.debounce';
  import {useEventListener} from '@vueuse/core';

  const surfaceEditorStore = useSurfaceEditorStore();
  const {surfaces, isUserSetScale, selectedElement, scale} = storeToRefs(surfaceEditorStore);
  const stageContainerEl = ref();
  const stageContentEl = ref();

  const {width: containerWidth, height: containerHeight} = useElementSize(stageContainerEl);
  const {width: contentWidth} = useElementSize(stageContentEl);
  const {x: scrollX, isScrolling} = useScroll(stageContainerEl);

  let userScrolled = false;
  let scrollingFromScale = false;

  const resetScrollingFromScale = debounce(() => (scrollingFromScale = false), 100);

  const initialized = ref(false); //stops surfaces from being rendered until we have prepared things

  // Detect user interaction
  useEventListener(stageContainerEl, 'scroll', () => {
    if (isScrolling.value && !scrollingFromScale) {
      userScrolled = true;
    }
  })

  // Whenever content width changes, recenter scroll
  watchEffect(() => {
    if (contentWidth.value <= containerWidth.value) {
      userScrolled = false;
    }

    if (!stageContainerEl.value || userScrolled) {
      return;
    }

    scrollingFromScale = true;
    scrollX.value = (contentWidth.value - containerWidth.value) / 2;
    resetScrollingFromScale();
  });

  function deselect() {
    surfaceEditorStore.closeContextMenu();
    surfaceEditorStore.closeContextualPanel();
    surfaceEditorStore.selectElement({element: null});
    surfaceEditorStore.editElement({element: null});
  }

  function snapScale(current) {
    return Math.pow(SURFACE_EDITOR_SCALE_MULTIPLIER, Math.round(Math.log(current) / Math.log(SURFACE_EDITOR_SCALE_MULTIPLIER)));
  }

  function zoom(current, direction) {
    const snapped = snapScale(current);
    const next = snapped * (direction > 0 ? SURFACE_EDITOR_SCALE_MULTIPLIER : 1 / SURFACE_EDITOR_SCALE_MULTIPLIER);
    return Math.min(SURFACE_EDITOR_MAX_SCALE, Math.max(SURFACE_EDITOR_MIN_SCALE, next));
  }

  function zoomIn() {
    surfaceEditorStore.setScale({
      scale: zoom(scale.value, +1),
      isUserSet: true,
    });
  }

  function zoomOut() {
    surfaceEditorStore.setScale({
      scale: zoom(scale.value, -1),
      isUserSet: true,
    });
  }

  function updateScale(targetScale) {
    if (targetScale === 'fit') {
      return surfaceEditorStore.setScaleForFit({
        containerWidth: containerWidth.value,
        containerHeight: containerHeight.value,
        isUserSet: true
      });
    }

    if (targetScale === 'fill') {
      return surfaceEditorStore.setScaleForFill({
        containerWidth: containerWidth.value,
        isUserSet: true
      });
    }

    return surfaceEditorStore.setScale({scale: targetScale, isUserSet: true});
  }

  defineExpose({
    updateScale,
    zoomIn,
    zoomOut
  });

  const hasActionableSelection = computed(() =>
    selectedElement.value
    && !selectedElement.value.background
  );

  function deleteSelected() {
    if (!hasActionableSelection.value) return;
    surfaceEditorStore.deleteElement({element: selectedElement.value});
  }

  function copySelected() {
    if (!hasActionableSelection.value) return;
    surfaceEditorStore.copyElement({element: selectedElement.value});
  }

  function pasteClipboard() {
    if (!hasActionableSelection.value) return;
    surfaceEditorStore.pasteElement();
  }

  function duplicateSelected() {
    if (!hasActionableSelection.value) return;
    surfaceEditorStore.duplicateElement({element: selectedElement.value});
  }

  function nudge(dx, dy) {
    if (!hasActionableSelection.value) return;
    surfaceEditorStore.nudgeElement({element: selectedElement.value, dx, dy});
  }

  function resize(delta) {
    if (!hasActionableSelection.value) return;
    const element = selectedElement.value;
    if (element?.type === SURFACE_EDITOR_ELEMENT_TYPE.shape) {
      surfaceEditorStore.resizeShapeElement({element, delta});
      return;
    }
    surfaceEditorStore.resizeElementProportionally({element, delta});
  }

  function isEditableTarget(target) {
    const el = target;
    if (!el) return false;
    return el.isContentEditable
      || el.closest?.('[contenteditable="true"]')
      || el.tagName === 'INPUT'
      || el.tagName === 'TEXTAREA'
      || el.tagName === 'SELECT';
  }

  useEventListener(window, 'keydown', (event) => {
    if (!hasActionableSelection.value) return;
    if (event.isComposing) return;
    if (isEditableTarget(event.target)) return;

    const key = event.key;
    const metaKey = event.metaKey || event.ctrlKey;

    if ((key === 'Delete' || key === 'Backspace') && !metaKey) {
      event.preventDefault();
      deleteSelected();
      return;
    }

    if (metaKey && key.toLowerCase() === 'c') {
      event.preventDefault();
      copySelected();
      return;
    }

    if (metaKey && key.toLowerCase() === 'v') {
      event.preventDefault();
      pasteClipboard();
      return;
    }

    if (metaKey && key.toLowerCase() === 'd') {
      event.preventDefault();
      duplicateSelected();
      return;
    }

    const isArrow = key === 'ArrowUp' || key === 'ArrowDown' || key === 'ArrowLeft' || key === 'ArrowRight';
    if (!isArrow) return;

    if (metaKey && (key === 'ArrowUp' || key === 'ArrowDown')) {
      event.preventDefault();
      if (event.shiftKey) {
        resize(key === 'ArrowUp' ? -50 : 50);
      } else {
        resize(key === 'ArrowUp' ? -10 : 10);
      }
      return;
    }

    if (metaKey) return;

    event.preventDefault();
    const distance = event.shiftKey ? 50 : 10;
    switch (key) {
      case 'ArrowUp':
        nudge(0, -distance);
        break;
      case 'ArrowDown':
        nudge(0, distance);
        break;
      case 'ArrowLeft':
        nudge(-distance, 0);
        break;
      case 'ArrowRight':
        nudge(distance, 0);
        break;
    }
  });

  //determine initial scale
  watch(containerWidth, () => {
    if (!isUserSetScale.value) {
      surfaceEditorStore.setScaleForFit({
        containerWidth: containerWidth.value,
        containerHeight: containerHeight.value,
        isUserSet: false
      });
    }
  },
  {
    immediate: true
  });

  onMounted(async () => {
    //todo: figure out the logic for this so it is smart
    surfaceEditorStore.setActiveSurface({surface: surfaces.value[0]});

    await waitFor(300);
    surfaceEditorStore.setScaleForFit({
      containerWidth: containerWidth.value,
      containerHeight: containerHeight.value,
      isUserSet: false
    });

    initialized.value = true;
  });

</script>
